import { nanoid } from "nanoid";
import {
  DndContext,
  TouchSensor,
  PointerSensor,
  useSensors,
  useSensor,
  MeasuringStrategy,
  DragOverlay,
  KeyboardSensor,
} from "@dnd-kit/core";
import { createPortal } from "react-dom";
import { noop } from "lodash-es";
import { useCallback, useMemo } from "react";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import Box from "ds/components/Box";
import { HookType } from "utils/hooks";
import useTwoColumnDND from "components/DragDropList/useTwoColumnDND";
import { dropAnimation, getCoordinateGetter } from "components/DragDropList/utils";
import { ColumnId } from "components/DragDropList/types";
import DragDropListOverlay from "components/DragDropList/Overlay";
import DragDropListDefaultItem from "components/DragDropList/DefaultItem";

import { Commands } from "./types";
import BeforeAfterCommandsList from "./List";
import Item from "./List/Item";

type BeforeAfterCommandsProps = {
  commands: Commands;
  onChange: (commands: Commands) => void;
  readOnly?: boolean;
  onAfterInputChange?: (value: string) => void;
  onBeforeInputChange?: (value: string) => void;
};

const BeforeAfterCommands = ({
  commands = { before: [], after: [] },
  onChange,
  readOnly,
  onAfterInputChange,
  onBeforeInputChange,
}: BeforeAfterCommandsProps) => {
  const handleAddCommands = useCallback(
    (type: HookType, lines: string[]) => {
      onChange({
        ...commands,
        [type]: [
          ...(commands[type] || []),
          ...lines.map((line) => ({
            text: line,
            id: nanoid(),
          })),
        ],
      });
    },
    [commands, onChange]
  );

  const allTextCommandsById: Record<string, string> = useMemo(() => {
    return {
      ...commands.before?.reduce((acc, command) => ({ ...acc, [command.id]: command.text }), {}),
      ...commands.after?.reduce((acc, command) => ({ ...acc, [command.id]: command.text }), {}),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commands, commands.before?.length, commands.after?.length]); // It allows to recalculate when references are not changed

  const firstColumn = useMemo(() => {
    return commands.before?.map((command) => ({
      value: command.id,
    }));
  }, [commands.before]);

  const secondColumn = useMemo(() => {
    return commands.after?.map((command) => ({
      value: command.id,
    }));
  }, [commands.after]);

  const handleRemoveCommand = useCallback(
    (type: HookType, id: string) =>
      onChange({
        ...commands,
        [type]: commands[type]?.filter((command) => command.id !== id) || [],
      }),
    [commands, onChange]
  );

  const handleEditCommand = useCallback(
    (type: HookType, id: string, text: string) => {
      onChange({
        ...commands,
        [type]: commands[type]?.map((command) =>
          command.id === id ? { ...command, text } : command
        ),
      });
    },
    [commands, onChange]
  );

  const coordinateGetter = useMemo(() => getCoordinateGetter(true), []);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter,
    })
  );

  const {
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    handleDragOver,
    handleDragMove,
    collisionDetection,
    firstContainerItems,
    secondContainerItems,
    isDragActive,
    activeElementId,
  } = useTwoColumnDND({
    onChange: ({ first, second }) => {
      onChange({
        before:
          first.map((item: { value: string }) => ({
            id: item.value,
            text: allTextCommandsById[item.value],
          })) || [],
        after:
          second.map((item: { value: string }) => ({
            id: item.value,
            text: allTextCommandsById[item.value],
          })) || [],
      });
    },
    firstColumn: firstColumn || [],
    secondColumn: secondColumn || [],
  });

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      onDragAbort={handleDragCancel}
      onDragCancel={handleDragCancel}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      modifiers={[restrictToVerticalAxis]}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
      collisionDetection={collisionDetection}
      onDragMove={handleDragMove}
    >
      <Box gap="medium" direction="column" fullWidth>
        {commands.before && (
          <BeforeAfterCommandsList
            type="before"
            columnId={ColumnId.First}
            title="Before"
            commandsIds={firstContainerItems}
            allTextCommandsById={allTextCommandsById}
            onAddCommands={handleAddCommands}
            onRemoveCommand={handleRemoveCommand}
            readOnly={readOnly}
            isDropZoneActive={isDragActive}
            onEditCommand={handleEditCommand}
            onChangeCallback={onAfterInputChange}
          />
        )}
        {commands.after && (
          <BeforeAfterCommandsList
            type="after"
            columnId={ColumnId.Second}
            title="After"
            commandsIds={secondContainerItems}
            allTextCommandsById={allTextCommandsById}
            onAddCommands={handleAddCommands}
            onRemoveCommand={handleRemoveCommand}
            readOnly={readOnly}
            isDropZoneActive={isDragActive}
            onEditCommand={handleEditCommand}
            onChangeCallback={onBeforeInputChange}
          />
        )}
      </Box>
      {createPortal(
        <DragOverlay dropAnimation={dropAnimation}>
          {activeElementId && (
            <DragDropListOverlay
              component={
                <DragDropListDefaultItem
                  titleComponent={
                    <Item
                      text={allTextCommandsById[activeElementId]}
                      readOnly={readOnly}
                      onRemoveCommand={noop}
                      onEditCommand={noop}
                      id={activeElementId}
                    />
                  }
                />
              }
            />
          )}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
};

export default BeforeAfterCommands;
